import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Layers, Palette, ShieldCheck, Send, LineChart, BarChart3,
  ChevronLeft, ChevronRight, Download, Circle,
} from "lucide-react";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { ErrorBanner, LoadingCard, Pill } from "../lib/ui";

/**
 * Meta App Review · Reviewer walkthrough surface.
 *
 * A single-frame slideshow that walks a Meta reviewer through the
 * end-to-end governed publishing flow, from Foundation through
 * Insights. Scene 1 is the flow diagram itself — exportable to PNG
 * so it can double as the static graphic in the App Review submission.
 *
 * The remaining scenes each render one stop in the flow with LIVE
 * data pulled from real Brand OS endpoints so the reviewer can see
 * the product actually operates as described. The read_insights case
 * lives in scene 6 with an on-camera call-out of Meta's granular
 * scope model.
 */

// ---------------------------------------------------------------------------
// Full-vision lifecycle diagram (Scene 1 · exportable to PNG)
//
// Detailed 7-stage lifecycle showing Brand OS end-to-end. Designed to
// match the density of a Meta App Review supporting-artwork submission
// while remaining truthful about what the product IS (multi-platform
// governed publishing) and where it's headed (7 platforms via one
// governance rail).
// ---------------------------------------------------------------------------

const LIFECYCLE_STAGES = [
  {
    number: "01", title: "BRAND OS INITIATION", subtitle: "Strategy & Brief",
    accent: "#4dd0e1",
    bullets: ["Campaign objective", "Audience definition", "Value proposition",
              "Budget & schedule", "Channel(s) & product(s)", "Brand guidelines"],
    outputs: ["Strategy Package (SHA-256)", "Approved Claims", "Audience Specs",
              "Creative Brief", "Compliance Guardrails"],
    gate: { label: "Gate 1–3", desc: "Strategy Approved" },
  },
  {
    number: "02", title: "AD DEVELOPMENT", subtitle: "Model-Assisted + Human Collaboration",
    accent: "#ab47bc",
    bullets: ["Brand OS Creative Studio", "Claim Validator", "Policy Pre-Screen",
              "Accessibility Check", "Hate / Profanity Check", "Health-Claims Sub-Check"],
    outputs: ["Ad Variants", "Primary Text", "Headlines", "Images / Videos",
              "CTAs", "Landing URL", "Tracking Parameters"],
    gate: { label: "Gate 4", desc: "Creative Approved" },
  },
  {
    number: "03", title: "QA & POLICY CHECKS", subtitle: "Automated + Human QA",
    accent: "#42a5f5",
    bullets: ["Multi-platform Policy Checker", "Restricted Content Scan",
              "Text-to-Image Compliance", "Personal Data Check",
              "Landing Page Review", "URL Safety Scan"],
    outputs: ["QA Report", "Policy Compliance Pass", "Risk Score",
              "Required Fixes", "Human QA Sign-off"],
    gate: { label: "Gate 4.5", desc: "QA Passed" },
  },
  {
    number: "04", title: "AUDIT & PROVENANCE", subtitle: "Immutable Audit Trail",
    accent: "#ff9800",
    bullets: ["Generate Audit Package", "Capture All Inputs / Outputs",
              "Store E1 Provenance Triple", "Hash All Assets (SHA-256)",
              "Record Approvals", "Map to Brand & Product"],
    outputs: ["Audit Package (ZIP)", "E1 Provenance Record",
              "Governance Report", "Approval Chain", "Ready to Publish"],
    gate: { label: "Gate 5", desc: "Audited & Approved" },
  },
  {
    number: "05", title: "PUBLISH", subtitle: "Multi-Platform via Governed APIs",
    accent: "#fdd835",
    bullets: ["Create Campaign / Ad Set / Ad", "Upload Creative & Copy",
              "Set Targeting & Audiences", "Set Budget & Schedule",
              "Apply Tracking (UTM + Conversions API)", "Final Pre-Publish Check"],
    outputs: ["Campaign ID", "Ad Set ID(s)", "Ad ID(s)",
              "Publish Confirmation", "Delivery Status: Active"],
    gate: { label: "Gate 5.1", desc: "Published" },
  },
  {
    number: "06", title: "MONITOR & COMPLY", subtitle: "Ongoing Monitoring",
    accent: "#66bb6a",
    bullets: ["Delivery Performance", "Policy Compliance Monitor",
              "Ad Rejection Alerts", "Comment Moderation",
              "Landing Page Uptime", "Spend & Pacing Monitor"],
    outputs: ["Impressions · Clicks · CTR", "CPC / CPM · Conversions",
              "Cost / Conversion · Frequency", "Relevance Score",
              "Policy Violations"],
    gate: { label: "Continuous", desc: "Monitoring" },
  },
  {
    number: "07", title: "FEEDBACK & OPTIMIZE", subtitle: "Learning & Optimization",
    accent: "#26c6da",
    bullets: ["Performance Attribution", "Creative / Audience Insights",
              "Rule & Policy Updates", "Brand OS Learning Models",
              "Human Review & Decisions", "Optimize or Refresh Variants"],
    outputs: ["Performance Report", "Insight Summary",
              "Optimization Recommendations", "Updated Strategy Inputs",
              "Feedback to Brand OS"],
    gate: { label: "Gate 6", desc: "Learn & Optimize" },
  },
];

const GOVERNANCE_RAILS = [
  { title: "DATA PRIVACY", items: ["No sale of user data", "Minimize data collection",
    "Use hashed / aggregated data", "Respect user consent", "Comply with platform Terms"] },
  { title: "MULTI-PLATFORM POLICY", items: ["Meta advertising policies", "LinkedIn ad standards",
    "TikTok community guidelines", "YouTube advertiser rules", "WhatsApp Business rules"] },
  { title: "SECURITY & ACCESS", items: ["Role-based access", "Least privilege",
    "2FA / SSO", "Encrypted in transit + at rest", "Secret vault + audit logs"] },
  { title: "AUDIT & TRANSPARENCY", items: ["Immutable append-only logs", "End-to-end traceability",
    "Human approvals recorded", "Timestamped actions", "Exportable audit reports"] },
  { title: "HUMAN OVERSIGHT", items: ["Humans approve strategy, creative, QA",
    "Humans review flagged issues", "Humans decide on significant changes",
    "Respect rate limits", "No scraping"] },
];

const PLATFORMS = [
  { key: "meta_fb", label: "Facebook", api: "Meta Graph + Marketing API" },
  { key: "meta_ig", label: "Instagram", api: "Meta Graph + Marketing API" },
  { key: "whatsapp", label: "WhatsApp", api: "WhatsApp Business API" },
  { key: "linkedin", label: "LinkedIn", api: "LinkedIn Marketing API" },
  { key: "tiktok", label: "TikTok", api: "TikTok Marketing API" },
  { key: "youtube", label: "YouTube", api: "YouTube Data + Google Ads" },
  { key: "google_ads", label: "Google Ads", api: "Google Ads API" },
];

const FEEDBACK_LOOPS = [
  { title: "Performance Insights", desc: "Data flows back to Brand OS for analysis and reporting" },
  { title: "Policy & Compliance", desc: "Platform policy changes are ingested and applied" },
  { title: "Creative & Audience", desc: "Winning patterns inform future creative and targeting" },
  { title: "Strategy Refinement", desc: "Outcomes refine goals, messaging, audiences, budget" },
];

function FullLifecycleDiagram({ svgRef = null }) {
  // -------------------------------------------------------------------
  // Layout: 2 rows of stage cards + governance band + platforms strip.
  //
  // Row 1: stages 01–04 (4 wide)
  // Row 2: stages 05–07 (3 wide) + governance summary card in slot 4
  //
  // ViewBox is tuned so every text element renders at a legible size
  // even when the SVG is scaled down to fit a browser window.
  // -------------------------------------------------------------------
  const W = 2000, H = 1960;
  const margin = 40;
  const cols = 4;
  const gap = 22;
  const stageW = (W - margin * 2 - gap * (cols - 1)) / cols; // ~468
  const stageH = 660;
  const row1Y = 160;
  const row2Y = row1Y + stageH + 44;
  const govY = row2Y + stageH + 60;
  const govH = 190;
  const platY = govY + govH + 50;
  const platH = 96;
  const footY = platY + platH + 46;

  const bgPage = "#0f1116";
  const bgCard = "#161a22";
  const border = "#2c313d";
  const textMain = "#ecd9b0";
  const textBody = "#c9cfda";
  const textMuted = "#8a92a6";
  const textDim = "#6b7080";
  const accentGold = "#c5a25f";

  const positions = LIFECYCLE_STAGES.map((s, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const x = margin + col * (stageW + gap);
    const y = row === 0 ? row1Y : row2Y;
    return { s, x, y, row, col, i };
  });

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${W} ${H}`}
      xmlns="http://www.w3.org/2000/svg"
      data-testid="walkthrough-flow-diagram"
      width="100%"
      style={{ maxWidth: "100%", height: "auto", background: bgPage,
                fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" }}
    >
      <defs>
        <marker id="lifecycleArrow" viewBox="0 0 12 12" refX="11" refY="6"
                markerWidth="10" markerHeight="10" orient="auto">
          <path d="M 0 0 L 12 6 L 0 12 z" fill={accentGold} />
        </marker>
      </defs>

      {/* ---------- Title band ---------- */}
      <text x={W / 2} y={64} textAnchor="middle" fill={textMain}
            style={{ font: "700 44px 'Playfair Display', Georgia, serif",
                     letterSpacing: 2 }}>
        BRAND OS · GOVERNED MULTI-PLATFORM PUBLISHING
      </text>
      <text x={W / 2} y={104} textAnchor="middle" fill={accentGold}
            style={{ font: "500 22px 'Figtree', sans-serif", letterSpacing: 3 }}>
        SEVEN STAGES · SEVEN PLATFORMS · ONE AUDIT TRAIL
      </text>

      {/* ---------- Stage cards (2 rows × 4 cols) ---------- */}
      {positions.map(({ s, x, y, col, i }) => {
        const stageAccent = s.accent;
        const shownBullets = s.bullets.slice(0, 3);
        const shownOutputs = s.outputs.slice(0, 3);
        return (
          <g key={s.number}>
            {/* Card outline */}
            <rect x={x} y={y} width={stageW} height={stageH}
                  rx={16} ry={16}
                  fill={bgCard} stroke={stageAccent} strokeWidth={2.5} />
            {/* Numbered header stripe */}
            <rect x={x} y={y} width={stageW} height={64}
                  rx={16} ry={16}
                  fill={stageAccent} opacity={0.14} />
            <text x={x + 24} y={y + 44} fill={stageAccent}
                  style={{ font: "700 34px 'Playfair Display', serif",
                           letterSpacing: 2 }}>
              {s.number}
            </text>
            <text x={x + 84} y={y + 34} fill={textMain}
                  style={{ font: "700 22px 'Figtree', sans-serif",
                           letterSpacing: 0.5 }}>
              {s.title}
            </text>
            <text x={x + 84} y={y + 56} fill={textMuted}
                  style={{ font: "500 16px 'Figtree', sans-serif" }}>
              {s.subtitle}
            </text>

            {/* Bullets */}
            <text x={x + 24} y={y + 108} fill={stageAccent}
                  style={{ font: "700 14px 'Figtree', sans-serif",
                           letterSpacing: 2.5 }}>
              WHAT HAPPENS
            </text>
            {shownBullets.map((b, bi) => (
              <g key={bi}>
                <circle cx={x + 30} cy={y + 132 + bi * 34}
                        r={4} fill={stageAccent} />
                <text x={x + 44} y={y + 138 + bi * 34}
                      fill={textBody}
                      fontFamily="Figtree, sans-serif"
                      fontSize="19"
                      fontWeight="500">
                  {b}
                </text>
              </g>
            ))}

            {/* Divider */}
            <line x1={x + 24} y1={y + 288} x2={x + stageW - 24} y2={y + 288}
                  stroke={border} strokeWidth={1.5} />

            {/* Outputs */}
            <text x={x + 24} y={y + 314} fill={stageAccent}
                  style={{ font: "700 14px 'Figtree', sans-serif",
                           letterSpacing: 2.5 }}>
              OUTPUTS
            </text>
            {shownOutputs.map((o, oi) => (
              <g key={oi}>
                <path d={`M ${x + 26} ${y + 336 + oi * 34}
                         l 8 6 l -8 6`}
                      fill="none" stroke={stageAccent} strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round" />
                <text x={x + 44} y={y + 344 + oi * 34}
                      fill={textMain}
                      fontFamily="Figtree, sans-serif"
                      fontSize="19"
                      fontWeight="500">
                  {o}
                </text>
              </g>
            ))}

            {/* Gate footer band */}
            <rect x={x} y={y + stageH - 92} width={stageW} height={92}
                  fill={stageAccent} opacity={0.14} />
            <line x1={x} y1={y + stageH - 92}
                  x2={x + stageW} y2={y + stageH - 92}
                  stroke={stageAccent} strokeWidth={1.5} opacity={0.6} />
            <text x={x + 24} y={y + stageH - 58} fill={stageAccent}
                  style={{ font: "700 15px 'Figtree', sans-serif",
                           letterSpacing: 2.5 }}>
              {s.gate.label.toUpperCase()}
            </text>
            <text x={x + 24} y={y + stageH - 26} fill={textMain}
                  style={{ font: "600 20px 'Playfair Display', serif" }}>
              {s.gate.desc}
            </text>

            {/* Right-arrow to next stage on same row */}
            {i < LIFECYCLE_STAGES.length - 1
              && (col < cols - 1)
              && (i !== 3) && (
              <g>
                <line x1={x + stageW + 2} y1={y + stageH / 2}
                      x2={x + stageW + gap - 4} y2={y + stageH / 2}
                      stroke={accentGold} strokeWidth={3}
                      markerEnd="url(#lifecycleArrow)" />
              </g>
            )}
          </g>
        );
      })}

      {/* Wrap arrow: end of row 1 → start of row 2 (04 → 05) */}
      <g>
        {(() => {
          const endStage = positions[3]; // stage 04
          const nextStage = positions[4]; // stage 05
          const x1 = endStage.x + stageW / 2;
          const y1 = endStage.y + stageH + 4;
          const y2 = nextStage.y - 4;
          const midY = (y1 + y2) / 2;
          const x2 = nextStage.x + stageW / 2;
          return (
            <>
              <path
                d={`M ${x1} ${y1} L ${x1} ${midY} L ${x2} ${midY} L ${x2} ${y2}`}
                fill="none" stroke={accentGold} strokeWidth={3}
                markerEnd="url(#lifecycleArrow)" />
              <text x={(x1 + x2) / 2} y={midY - 8} textAnchor="middle"
                    fill={accentGold}
                    style={{ font: "600 15px 'Figtree', sans-serif",
                             letterSpacing: 2 }}>
                AUDIT PACKAGE HANDS OFF TO PUBLISH
              </text>
            </>
          );
        })()}
      </g>

      {/* ---------- Row-2 slot 4: Governance summary card ---------- */}
      {(() => {
        const gx = margin + 3 * (stageW + gap);
        const gy = row2Y;
        return (
          <g key="governance-summary">
            <rect x={gx} y={gy} width={stageW} height={stageH}
                  rx={16} ry={16}
                  fill="#1a1d24" stroke={accentGold} strokeWidth={2.5} />
            <rect x={gx} y={gy} width={stageW} height={64}
                  rx={16} ry={16} fill={accentGold} opacity={0.16} />
            <text x={gx + 24} y={gy + 40} fill={accentGold}
                  style={{ font: "700 26px 'Playfair Display', serif",
                           letterSpacing: 2 }}>
              ▲ GOVERNANCE
            </text>
            <text x={gx + 24} y={gy + 100} fill={textMain}
                  style={{ font: "700 15px 'Figtree', sans-serif",
                           letterSpacing: 2.5 }}>
              APPLIED AT EVERY STAGE
            </text>
            {[
              "Human approves strategy, creative, QA",
              "Immutable SHA-256 audit for every artifact",
              "Role-based access + encrypted secrets",
              "Platform policy pre-checks (Meta, LinkedIn, TikTok, YouTube, WhatsApp, Google)",
              "Data minimization + user consent respected",
              "Rate limits honoured. No scraping.",
              "Continuous compliance monitoring",
            ].map((line, li) => (
              <g key={li}>
                <circle cx={gx + 30} cy={gy + 134 + li * 34}
                        r={4} fill={accentGold} />
                <text x={gx + 44} y={gy + 140 + li * 34}
                      fill={textBody}
                      fontFamily="Figtree, sans-serif"
                      fontSize="18"
                      fontWeight="500">
                  {line}
                </text>
              </g>
            ))}
            <line x1={gx + 24} y1={gy + stageH - 92 - 6}
                  x2={gx + stageW - 24} y2={gy + stageH - 92 - 6}
                  stroke={border} strokeWidth={1.5} />
            <text x={gx + 24} y={gy + stageH - 58} fill={accentGold}
                  style={{ font: "700 15px 'Figtree', sans-serif",
                           letterSpacing: 2.5 }}>
              INVARIANT
            </text>
            <text x={gx + 24} y={gy + stageH - 26} fill={textMain}
                  style={{ font: "600 20px 'Playfair Display', serif" }}>
              No gate skipped · No exception silent
            </text>
          </g>
        );
      })()}

      {/* ---------- Governance rails detail band ---------- */}
      <rect x={margin} y={govY} width={W - margin * 2} height={govH}
            rx={16} ry={16}
            fill={bgCard} stroke={border} strokeWidth={1.5} />
      <text x={W / 2} y={govY + 40} textAnchor="middle" fill={accentGold}
            style={{ font: "700 22px 'Playfair Display', serif",
                     letterSpacing: 3 }}>
        GOVERNANCE RAILS · FIVE ALWAYS-ON POLICIES
      </text>
      {GOVERNANCE_RAILS.map((rail, ri) => {
        const cellW = (W - margin * 2 - 32) / GOVERNANCE_RAILS.length;
        const cx = margin + 16 + ri * cellW;
        const cy = govY + 78;
        // Compact: show top 3 items only, larger font
        const items = rail.items.slice(0, 3);
        return (
          <g key={rail.title}>
            <text x={cx} y={cy} fill={textMain}
                  style={{ font: "700 16px 'Figtree', sans-serif",
                           letterSpacing: 1.5 }}>
              {rail.title}
            </text>
            {items.map((it, ii) => (
              <g key={ii}>
                <circle cx={cx + 6} cy={cy + 20 + ii * 24}
                        r={3} fill={textMuted} />
                <text x={cx + 18} y={cy + 26 + ii * 24}
                      fill={textMuted}
                      fontFamily="Figtree, sans-serif"
                      fontSize="15"
                      fontWeight="500">
                  {it}
                </text>
              </g>
            ))}
          </g>
        );
      })}

      {/* ---------- Platforms strip ---------- */}
      <text x={margin} y={platY - 14} fill={accentGold}
            style={{ font: "700 18px 'Figtree', sans-serif", letterSpacing: 3 }}>
        SEVEN PLATFORMS · ONE GOVERNANCE RAIL
      </text>
      {PLATFORMS.map((p, pi) => {
        const cellW = (W - margin * 2 - (PLATFORMS.length - 1) * 10)
                       / PLATFORMS.length;
        const px = margin + pi * (cellW + 10);
        return (
          <g key={p.key}>
            <rect x={px} y={platY} width={cellW} height={platH}
                  rx={12} ry={12}
                  fill={bgCard} stroke={border} strokeWidth={1.5} />
            <text x={px + cellW / 2} y={platY + 42} textAnchor="middle"
                  fill={textMain}
                  style={{ font: "700 22px 'Playfair Display', serif" }}>
              {p.label}
            </text>
            <text x={px + cellW / 2} y={platY + 72} textAnchor="middle"
                  fill={textDim}
                  style={{ font: "500 14px 'Figtree', sans-serif" }}>
              {p.api}
            </text>
          </g>
        );
      })}

      {/* ---------- Commitment footer ---------- */}
      <text x={W / 2} y={footY} textAnchor="middle" fill={textMuted}
            style={{ font: "600 18px 'Figtree', sans-serif", letterSpacing: 2 }}>
        We respect users · We follow the rules · We protect data · We deliver value
      </text>
      <text x={W / 2} y={footY + 30} textAnchor="middle" fill={textDim}
            style={{ font: "500 15px 'Figtree', sans-serif", letterSpacing: 1.5 }}>
        Privacy by Design · Governance by Default · Transparency Always · Humans in Control
      </text>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Compact strip diagram (Scenes 2-8 · lightweight per-stage highlight)
// ---------------------------------------------------------------------------

const FLOW_STAGES = [
  {
    key: "foundation", label: "Foundation",
    caption: "Brand voice, audience, products",
    icon: Layers,
  },
  {
    key: "create", label: "Create",
    caption: "Campaigns and asset sets",
    icon: Palette,
  },
  {
    key: "preflight", label: "Preflight",
    caption: "Governance check + freeze",
    icon: ShieldCheck,
  },
  {
    key: "publish", label: "Publish",
    caption: "Meta Graph · Email · Export",
    icon: Send,
  },
  {
    key: "attribute", label: "Attribute",
    caption: "read_insights · engagement",
    icon: BarChart3,
  },
  {
    key: "insights", label: "Insights",
    caption: "Cost per outcome per variant",
    icon: LineChart,
  },
];

// Render the flow as a real SVG so we can export it cleanly.
function FlowDiagramSvg({ activeKey = null, svgRef = null }) {
  const boxW = 170;
  const boxH = 96;
  const gap = 26;
  const cols = FLOW_STAGES.length;
  const width = cols * boxW + (cols - 1) * gap + 32;
  const height = boxH + 84;

  return (
    <svg
      ref={svgRef}
      width="100%"
      viewBox={`0 0 ${width} ${height}`}
      xmlns="http://www.w3.org/2000/svg"
      data-testid="walkthrough-flow-diagram"
      style={{ maxWidth: "100%", height: "auto" }}
    >
      <defs>
        <marker id="arrowhead" viewBox="0 0 10 10" refX="9" refY="5"
                markerWidth="8" markerHeight="8" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#c5a25f" />
        </marker>
      </defs>

      {/* connecting arrows */}
      {FLOW_STAGES.slice(0, -1).map((s, i) => {
        const x1 = 16 + (i + 1) * boxW + i * gap;
        const x2 = 16 + (i + 1) * boxW + (i + 1) * gap;
        const y = 20 + boxH / 2;
        return (
          <line
            key={`arrow-${i}`}
            x1={x1} y1={y} x2={x2 - 2} y2={y}
            stroke="#c5a25f" strokeWidth="2"
            markerEnd="url(#arrowhead)"
          />
        );
      })}

      {/* stage boxes */}
      {FLOW_STAGES.map((s, i) => {
        const x = 16 + i * (boxW + gap);
        const active = activeKey === s.key;
        return (
          <g key={s.key}>
            <rect
              x={x} y={20}
              width={boxW} height={boxH} rx="14" ry="14"
              fill={active ? "#c5a25f" : "#1a1d24"}
              stroke={active ? "#c5a25f" : "#2c313d"}
              strokeWidth="1.5"
            />
            <text
              x={x + boxW / 2} y={20 + 32}
              textAnchor="middle"
              fill={active ? "#0f1116" : "#e7d9b7"}
              style={{ font: "600 15px 'Playfair Display', serif" }}
            >
              {s.label}
            </text>
            <text
              x={x + boxW / 2} y={20 + 56}
              textAnchor="middle"
              fill={active ? "#0f1116" : "#8a92a6"}
              style={{ font: "400 11px 'Figtree', sans-serif" }}
            >
              {s.caption.slice(0, 30)}
            </text>
            <text
              x={x + boxW / 2} y={20 + 72}
              textAnchor="middle"
              fill={active ? "#0f1116" : "#8a92a6"}
              style={{ font: "400 11px 'Figtree', sans-serif" }}
            >
              {s.caption.length > 30 ? s.caption.slice(30) : ""}
            </text>
            <text
              x={x + boxW / 2} y={20 + boxH + 20}
              textAnchor="middle"
              fill="#8a92a6"
              style={{ font: "500 11px 'Figtree', sans-serif", letterSpacing: 1.5 }}
            >
              {String(i + 1).padStart(2, "0")}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// -------------------------------------------------------------------
// Sprint · Meta App Review · Sibling graphic (Scene 2 · exportable).
// ------------------------------------------------------------------
// This SVG matches the reference infographic shared by the owner:
// "BRAND OS × META — GOVERNED ADVERTISING & DATA LIFECYCLE".
//
// Purpose: explain to Meta *how we handle their data* — retention,
// storage, security, and purpose limitations — alongside the 7-stage
// governance lifecycle. This graphic focuses on the *data & integration
// boundary*, whereas Scene 1 focuses on the *governance lifecycle*.
// -------------------------------------------------------------------

// Compact bullet lists for the 7 stages, tuned for this graphic's
// narrower stage-card width (there is more content below the row).
const META_STAGES = [
  {
    n: "01", title: "BRAND OS INITIATION", subtitle: "Strategy & Brief",
    accent: "#34d1c0",
    happens: [
      "Strategy & Brief",
      "Audience Definition",
      "Value Proposition",
      "Budget & Schedule",
      "Brand Guidelines",
    ],
    outputs: ["Strategy Package (SHA-256)", "Approved Claims", "Audience Specs"],
    gate: { label: "GATE 1–3", desc: "Strategy Approved" },
  },
  {
    n: "02", title: "AD DEVELOPMENT", subtitle: "Model-Assisted + Human Collaboration",
    accent: "#c084fc",
    happens: [
      "Brand OS Creative Studio",
      "Claim Validator",
      "Policy Pre-Screen",
      "Accessibility & Safety Check",
    ],
    outputs: ["Ad Variants", "Primary Text", "Headline", "Images / Videos", "CTAs"],
    gate: { label: "GATE 4", desc: "Creative Approved" },
  },
  {
    n: "03", title: "QA & POLICY CHECKS", subtitle: "Automated + Human QA",
    accent: "#60a5fa",
    happens: [
      "Multi-platform Policy Checker",
      "Restricted Content Scan",
      "Text-to-Image Compliance",
      "Landing Page Review",
      "URL Safety Scan",
      "Human QA Sign-off",
    ],
    outputs: ["QA Report", "Policy Compliance Pass", "Risk Score", "Required Fixes (if any)"],
    gate: { label: "GATE 4.5", desc: "QA Passed" },
  },
  {
    n: "04", title: "AUDIT & PROVENANCE", subtitle: "Immutable Audit Trail",
    accent: "#f97316",
    happens: [
      "Generate Audit Package",
      "Capture All Inputs / Outputs",
      "Store E1 Provenance Triple (Evidence)",
      "Hash All Assets (SHA-256)",
      "Record Approvals",
      "Map to Brand & Product Policies",
    ],
    outputs: ["Audit Package (ZIP)", "E1 Provenance Record", "Governance Report", "Approval Chain"],
    gate: { label: "GATE 5", desc: "Audited & Approved" },
  },
  {
    n: "05", title: "PUBLISH TO META", subtitle: "Meta Marketing API",
    accent: "#f5b544",
    happens: [
      "Create Campaign",
      "Create Ad Set(s)",
      "Upload Creative",
      "Set Targeting",
      "Set Budget & Schedule",
      "Apply Tracking (UTM + Conversions API)",
      "Final Pre-Publish Check",
    ],
    outputs: ["Campaign ID", "Ad Set ID(s)", "Ad ID(s)", "Publish Confirmation", "Delivery Status: Active"],
    gate: { label: "GATE 5.1", desc: "Published" },
  },
  {
    n: "06", title: "MONITOR & COMPLY", subtitle: "Ongoing Monitoring",
    accent: "#22c55e",
    happens: [
      "Delivery Performance",
      "Policy Compliance Monitor",
      "Ad Rejection Alerts",
      "Comment Moderation (if applicable)",
      "Spend & Pacing Monitor",
      "Landing Page Uptime",
    ],
    outputs: [
      "Impressions",
      "Clicks (Link / CTR)",
      "CPC / CPM",
      "Conversions",
      "Cost / Conversion",
      "Frequency / Relevance",
      "Policy Violations",
    ],
    gate: { label: "CONTINUOUS", desc: "Monitoring" },
    outputsHeader: "MONITORED KPIs",
  },
  {
    n: "07", title: "FEEDBACK & OPTIMIZE", subtitle: "Learning & Optimization",
    accent: "#38bdf8",
    happens: [
      "Performance Attribution",
      "Creative / Audience Insights",
      "Policy-change Ingestion (Meta Updates)",
      "Human Review & Decisions",
      "Optimization Recommendations",
      "Refresh Variants",
    ],
    outputs: ["Performance Report", "Insight Summary", "Optimization Recommendations", "Updated Strategy Inputs"],
    gate: { label: "GATE 6", desc: "Learn & Optimize" },
  },
];

const META_DATA_PRINCIPLES = [
  {
    title: "DATA WE RECEIVE FROM META",
    color: "#38bdf8",
    items: [
      "Campaign delivery metrics",
      "Engagement data (aggregated)",
      "Conversion events (via CAPI)",
      "Policy alerts / rejections",
      "Ad review & account status",
    ],
  },
  {
    title: "DATA WE STORE",
    color: "#22c55e",
    items: [
      "Aggregated performance metrics",
      "Campaign / Ad IDs & status",
      "Optimization insights",
      "Audit & provenance records",
      "Logs & governance evidence",
    ],
  },
  {
    title: "DATA WE DO NOT RECEIVE OR STORE",
    color: "#ef4444",
    xIcon: true,
    items: [
      "User personal identifiers (PII)",
      "User email, phone, addresses",
      "Full device identifiers",
      "Raw browsing or behavior data",
      "Data from outside Meta without consent",
    ],
  },
  {
    title: "RETENTION & DELETION",
    color: "#c084fc",
    items: [
      "Performance data: up to 18 months",
      "Logs & audit: up to 7 years",
      "Then securely deleted or anonymized",
      "On-demand deletion available",
      "Follows Meta Data Use & Deletion Policies",
    ],
  },
  {
    title: "DATA SECURITY",
    color: "#34d1c0",
    items: [
      "Encryption in transit (TLS 1.2+)",
      "Encryption at rest (AES-256)",
      "Role-based access control",
      "Least privilege access",
      "Regular security reviews",
      "Audit logging (immutable)",
    ],
  },
  {
    title: "PURPOSE LIMITATION",
    color: "#f5b544",
    items: [
      "Use data only for authorized ad performance & optimization",
      "No selling or sharing",
      "No re-identification",
      "No cross-purpose use",
      "Comply with Meta Platform Terms",
    ],
  },
];

const META_COMPLIANCE_PILLARS = [
  {
    title: "HUMAN OVERSIGHT AT EVERY CRITICAL STEP",
    color: "#22c55e",
    items: [
      "Humans approve strategy, creative, targeting, budget, and publish",
      "Humans review policy issues, alerts, and critical optimizations",
      "No autonomous budget changes without approval",
      "No policy bypass, ever",
    ],
  },
  {
    title: "AUDIT & TRANSPARENCY",
    color: "#c084fc",
    items: [
      "Immutable SHA-256 audit for every artifact",
      "End-to-end traceability across all stages",
      "Evidence available for Meta review on request",
      "Role-based access to all records",
    ],
  },
  {
    title: "COMPLIANCE COMMITMENTS",
    color: "#60a5fa",
    items: [
      "We comply with Meta Platform Terms, Ad Policies, and Data Use Terms",
      "We honor user privacy and platform permissions",
      "We do not circumvent or evade any system or policy",
      "We design for trust, safety, and long-term partnership",
    ],
  },
  {
    title: "OVERSIGHT OWNERS",
    color: "#f5b544",
    items: [
      "Accountable humans with clear authority and escalation paths.",
    ],
  },
];

function DataLifecycleDiagram({ svgRef = null }) {
  // Landscape 2400x1600 -- big enough for retina PNG export (4800x3200)
  // and still legible at typical browser widths.
  const W = 2400, H = 1600;
  const margin = 28;
  const bg = "#0a0d13";
  const bgCard = "#141821";
  const bgCardAlt = "#191d28";
  const border = "#232937";
  const textMain = "#e8edf5";
  const textBody = "#b8c1d1";
  const textMuted = "#7f8899";
  const textDim = "#59606d";
  const gold = "#c5a25f";

  // ---- Layout regions ----
  const titleY = 70;
  const subY = 108;
  const microY = 140;

  // 7 stage cards row
  const stageRowY = 170;
  const stageGap = 12;
  const stageH = 400;
  const stageW = (W - margin * 2 - stageGap * 6) / 7;

  // Meta integration boundary
  const metaBandY = stageRowY + stageH + 42;
  const metaBandH = 340;

  // Data handling principles
  const dhY = metaBandY + metaBandH + 36;
  const dhH = 220;

  // Compliance pillars
  const compY = dhY + dhH + 26;
  const compH = 178;

  // Learning-loop footer
  const loopY = compY + compH + 20;
  const loopH = 60;

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${W} ${H}`}
      xmlns="http://www.w3.org/2000/svg"
      data-testid="walkthrough-flow-diagram"
      width="100%"
      style={{ maxWidth: "100%", height: "auto", background: bg }}
    >
      <defs>
        <marker id="mArrow" viewBox="0 0 10 10" refX="9" refY="5"
                markerWidth="8" markerHeight="8" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={gold} />
        </marker>
        <marker id="mArrowCyan" viewBox="0 0 10 10" refX="9" refY="5"
                markerWidth="8" markerHeight="8" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#34d1c0" />
        </marker>
        <marker id="mArrowGreen" viewBox="0 0 10 10" refX="9" refY="5"
                markerWidth="8" markerHeight="8" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#22c55e" />
        </marker>
      </defs>

      {/* ---------- Title / subtitle / rule strip ---------- */}
      <text x={W / 2} y={titleY} textAnchor="middle" fill={textMain}
            fontFamily="'Playfair Display', Georgia, serif"
            fontSize="52" fontWeight="700" letterSpacing="2">
        BRAND OS × META — GOVERNED ADVERTISING & DATA LIFECYCLE
      </text>
      <text x={W / 2} y={subY} textAnchor="middle" fill="#34d1c0"
            fontFamily="Figtree, sans-serif"
            fontSize="22" fontWeight="500" letterSpacing="1">
        Automation with Accountability.  Performance with Permission.  Growth with Governance.
      </text>
      <text x={W / 2} y={microY} textAnchor="middle" fill={textMuted}
            fontFamily="Figtree, sans-serif"
            fontSize="16" fontWeight="600" letterSpacing="3">
        THE GOVERNED LIFECYCLE:  NO GATE SKIPPED  ·  NO EXCEPTION SILENT
      </text>

      {/* ---------- Stage cards row ---------- */}
      {META_STAGES.map((s, i) => {
        const x = margin + i * (stageW + stageGap);
        const y = stageRowY;
        const outHdr = s.outputsHeader || "OUTPUTS";
        return (
          <g key={s.n}>
            {/* Card */}
            <rect x={x} y={y} width={stageW} height={stageH}
                  rx={10} ry={10}
                  fill={bgCard} stroke={s.accent} strokeWidth={1.6} />
            {/* Header stripe */}
            <rect x={x} y={y} width={stageW} height={54}
                  rx={10} ry={10}
                  fill={s.accent} opacity={0.14} />
            <text x={x + 14} y={y + 34} fill={s.accent}
                  fontFamily="'Playfair Display', serif"
                  fontSize="24" fontWeight="700" letterSpacing="1.5">
              {s.n}
            </text>
            <text x={x + 56} y={y + 26} fill={textMain}
                  fontFamily="Figtree, sans-serif"
                  fontSize="14" fontWeight="700" letterSpacing="0.5">
              {s.title}
            </text>
            <text x={x + 56} y={y + 44} fill={textMuted}
                  fontFamily="Figtree, sans-serif"
                  fontSize="12" fontWeight="500">
              {s.subtitle}
            </text>

            {/* WHAT HAPPENS list */}
            {s.happens.map((h, hi) => (
              <g key={hi}>
                <circle cx={x + 18} cy={y + 74 + hi * 21}
                        r={2.5} fill={s.accent} />
                <text x={x + 28} y={y + 78 + hi * 21}
                      fill={textBody}
                      fontFamily="Figtree, sans-serif"
                      fontSize="12.5" fontWeight="500">
                  {h}
                </text>
              </g>
            ))}

            {/* Outputs (positioned below the happens list) */}
            {(() => {
              const outStartY = y + 74 + s.happens.length * 21 + 12;
              return (
                <g>
                  <text x={x + 14} y={outStartY} fill={s.accent}
                        fontFamily="Figtree, sans-serif"
                        fontSize="11" fontWeight="700" letterSpacing="1.5">
                    {outHdr}
                  </text>
                  {s.outputs.map((o, oi) => (
                    <g key={oi}>
                      <path d={`M ${x + 16} ${outStartY + 14 + oi * 20}
                               l 6 4 l -6 4`}
                            fill="none" stroke={s.accent} strokeWidth={1.6}
                            strokeLinecap="round" strokeLinejoin="round" />
                      <text x={x + 30} y={outStartY + 20 + oi * 20}
                            fill={textMain}
                            fontFamily="Figtree, sans-serif"
                            fontSize="12.5" fontWeight="500">
                        {o}
                      </text>
                    </g>
                  ))}
                </g>
              );
            })()}

            {/* Gate footer band at bottom of card */}
            <rect x={x} y={y + stageH - 56} width={stageW} height={56}
                  fill={s.accent} opacity={0.14} />
            <rect x={x} y={y + stageH - 56} width={stageW} height={2}
                  fill={s.accent} opacity={0.5} />
            <text x={x + 14} y={y + stageH - 32} fill={s.accent}
                  fontFamily="Figtree, sans-serif"
                  fontSize="12" fontWeight="700" letterSpacing="1.5">
              {s.gate.label}
            </text>
            <text x={x + 14} y={y + stageH - 12} fill={textMain}
                  fontFamily="'Playfair Display', serif"
                  fontSize="15" fontWeight="600">
              {s.gate.desc}
            </text>
          </g>
        );
      })}

      {/* Arrow across the top of stages (implicit sequence) */}
      <path d={`M ${margin - 6} ${stageRowY + 20}
               L ${margin - 6} ${stageRowY - 24}
               L ${W - margin + 6} ${stageRowY - 24}
               L ${W - margin + 6} ${stageRowY + 20}`}
            fill="none" stroke={gold} strokeWidth={1.2} opacity={0.4}
            strokeDasharray="4 4" />

      {/* ================================================================= */}
      {/* Meta Integration Boundary                                          */}
      {/* ================================================================= */}
      <rect x={margin} y={metaBandY} width={W - margin * 2} height={metaBandH}
            rx={12} ry={12} fill={bgCardAlt} stroke={border} strokeWidth={1.5} />
      <text x={W / 2} y={metaBandY + 34} textAnchor="middle" fill="#34d1c0"
            fontFamily="Figtree, sans-serif"
            fontSize="20" fontWeight="700" letterSpacing="3">
        META INTEGRATION BOUNDARY
      </text>

      {/* Split into 5 conceptual columns:
           1. Brand OS (our system) 2. Brand OS API 3. Meta Systems
           4. Meta Platforms 5. Brand OS Receives */}
      {(() => {
        const bandInnerY = metaBandY + 54;
        const bandInnerH = metaBandH - 68;
        const cols = 5;
        const gutter = 90;
        const innerW = W - margin * 2 - gutter * (cols + 1);
        const colWs = [innerW * 0.24, innerW * 0.15, innerW * 0.22, innerW * 0.19, innerW * 0.20];
        const colXs = [];
        {
          let cursor = margin + gutter;
          for (const w of colWs) { colXs.push(cursor); cursor += w + gutter; }
        }
        return (
          <g>
            {/* Col 1: Brand OS (our system) */}
            <rect x={colXs[0]} y={bandInnerY} width={colWs[0]} height={bandInnerH}
                  rx={10} ry={10} fill="#0e1620" stroke="#34d1c0" strokeWidth={1.4} />
            <text x={colXs[0] + colWs[0] / 2} y={bandInnerY + 30} textAnchor="middle"
                  fill="#34d1c0"
                  fontFamily="Figtree, sans-serif"
                  fontSize="18" fontWeight="700" letterSpacing="1.5">
              BRAND OS  (OUR SYSTEM)
            </text>
            {[
              "Initiates and governs campaigns",
              "Generates and submits via Meta APIs",
              "Stores only permitted data",
              "Aggregates performance for insights",
              "Feeds learning back to Brand OS (governed loop)",
            ].map((line, li) => (
              <g key={li}>
                <circle cx={colXs[0] + 26} cy={bandInnerY + 60 + li * 22}
                        r={3} fill="#34d1c0" />
                <text x={colXs[0] + 38} y={bandInnerY + 64 + li * 22}
                      fill={textBody}
                      fontFamily="Figtree, sans-serif"
                      fontSize="13" fontWeight="500">
                  {line}
                </text>
              </g>
            ))}
            <rect x={colXs[0] + 20} y={bandInnerY + bandInnerH - 42}
                  width={colWs[0] - 40} height={30}
                  rx={6} ry={6} fill="#34d1c0" opacity={0.16} />
            <text x={colXs[0] + colWs[0] / 2} y={bandInnerY + bandInnerH - 22}
                  textAnchor="middle" fill="#34d1c0"
                  fontFamily="Figtree, sans-serif"
                  fontSize="13" fontWeight="700" letterSpacing="2">
              FULLY GOVERNED HERE
            </text>

            {/* Col 2: Secure API / Brand OS via Meta Marketing API */}
            <rect x={colXs[1]} y={bandInnerY} width={colWs[1]} height={bandInnerH}
                  rx={10} ry={10} fill="#0e1620" stroke={border} strokeWidth={1.3} />
            <text x={colXs[1] + colWs[1] / 2} y={bandInnerY + 60} textAnchor="middle"
                  fill={textMain}
                  fontFamily="'Playfair Display', serif"
                  fontSize="18" fontWeight="700">
              Brand OS
            </text>
            <text x={colXs[1] + colWs[1] / 2} y={bandInnerY + 84} textAnchor="middle"
                  fill={textMuted}
                  fontFamily="Figtree, sans-serif"
                  fontSize="12" fontWeight="500">
              via Meta Marketing API
            </text>
            {/* padlock symbol */}
            <g transform={`translate(${colXs[1] + colWs[1] / 2 - 12} ${bandInnerY + 120})`}>
              <rect x="0" y="6" width="24" height="18" rx="3" ry="3"
                    fill="none" stroke={gold} strokeWidth={1.6} />
              <path d="M 4 6 Q 4 0 12 0 Q 20 0 20 6"
                    fill="none" stroke={gold} strokeWidth={1.6} />
            </g>
            <text x={colXs[1] + colWs[1] / 2} y={bandInnerY + 168}
                  textAnchor="middle" fill={textMuted}
                  fontFamily="Figtree, sans-serif"
                  fontSize="12" fontWeight="600" letterSpacing="1">
              Secure API
            </text>
            <text x={colXs[1] + colWs[1] / 2} y={bandInnerY + 186}
                  textAnchor="middle" fill={textDim}
                  fontFamily="Figtree, sans-serif"
                  fontSize="11" fontWeight="500">
              (TLS 1.2+)
            </text>

            {/* Col 3: Meta Systems Validation & Delivery */}
            <rect x={colXs[2]} y={bandInnerY} width={colWs[2]} height={bandInnerH}
                  rx={10} ry={10} fill="#0e1620" stroke={border} strokeWidth={1.3} />
            <text x={colXs[2] + colWs[2] / 2} y={bandInnerY + 34} textAnchor="middle"
                  fill={textMain}
                  fontFamily="'Playfair Display', serif"
                  fontSize="18" fontWeight="700">
              Meta Systems
            </text>
            <text x={colXs[2] + colWs[2] / 2} y={bandInnerY + 56} textAnchor="middle"
                  fill={textMuted}
                  fontFamily="Figtree, sans-serif"
                  fontSize="13" fontWeight="500">
              Validation & Delivery
            </text>
            {[
              "Policy Enforcement",
              "Ad Review (if triggered)",
              "Delivery Infrastructure",
            ].map((line, li) => (
              <g key={li}>
                <circle cx={colXs[2] + 26} cy={bandInnerY + 100 + li * 24}
                        r={3} fill="#60a5fa" />
                <text x={colXs[2] + 38} y={bandInnerY + 104 + li * 24}
                      fill={textBody}
                      fontFamily="Figtree, sans-serif"
                      fontSize="13" fontWeight="500">
                  {line}
                </text>
              </g>
            ))}
            <text x={colXs[2] + colWs[2] / 2} y={bandInnerY + bandInnerH - 22}
                  textAnchor="middle" fill={textMuted}
                  fontFamily="Figtree, sans-serif"
                  fontSize="12" fontWeight="600" letterSpacing="1">
              Meta’s Systems & Controls
            </text>

            {/* Col 4: Meta Platforms (fb / ig) */}
            <rect x={colXs[3]} y={bandInnerY} width={colWs[3]} height={bandInnerH}
                  rx={10} ry={10} fill="#0e1620" stroke={border} strokeWidth={1.3} />
            <text x={colXs[3] + colWs[3] / 2} y={bandInnerY + 34} textAnchor="middle"
                  fill={textMain}
                  fontFamily="'Playfair Display', serif"
                  fontSize="18" fontWeight="700">
              facebook · instagram
            </text>
            <text x={colXs[3] + colWs[3] / 2} y={bandInnerY + 58} textAnchor="middle"
                  fill={textMuted}
                  fontFamily="Figtree, sans-serif"
                  fontSize="13" fontWeight="500">
              Meta Platforms (Facebook + Instagram)
            </text>
            {[
              "Serve Ads to Audiences",
              "Collect Interaction Signals",
              "Enforce Platform Policies",
            ].map((line, li) => (
              <g key={li}>
                <circle cx={colXs[3] + 26} cy={bandInnerY + 100 + li * 24}
                        r={3} fill="#c084fc" />
                <text x={colXs[3] + 38} y={bandInnerY + 104 + li * 24}
                      fill={textBody}
                      fontFamily="Figtree, sans-serif"
                      fontSize="13" fontWeight="500">
                  {line}
                </text>
              </g>
            ))}
            <text x={colXs[3] + colWs[3] / 2} y={bandInnerY + bandInnerH - 22}
                  textAnchor="middle" fill={textMuted}
                  fontFamily="Figtree, sans-serif"
                  fontSize="12" fontWeight="600" letterSpacing="1">
              User Experience
            </text>

            {/* Col 5: Brand OS Receives & Processes */}
            <rect x={colXs[4]} y={bandInnerY} width={colWs[4]} height={bandInnerH}
                  rx={10} ry={10} fill="#0e1620" stroke="#22c55e" strokeWidth={1.4} />
            <text x={colXs[4] + colWs[4] / 2} y={bandInnerY + 34} textAnchor="middle"
                  fill="#22c55e"
                  fontFamily="Figtree, sans-serif"
                  fontSize="15" fontWeight="700" letterSpacing="1.5">
              BRAND OS RECEIVES & PROCESSES
            </text>
            {[
              "Ingest Performance & Signals",
              "Monitor Compliance",
              "Analyze & Attribute",
              "Generate Insights",
              "Provide Optimization Recommendations",
            ].map((line, li) => (
              <g key={li}>
                <circle cx={colXs[4] + 20} cy={bandInnerY + 74 + li * 24}
                        r={3} fill="#22c55e" />
                <text x={colXs[4] + 32} y={bandInnerY + 78 + li * 24}
                      fill={textBody}
                      fontFamily="Figtree, sans-serif"
                      fontSize="13" fontWeight="500">
                  {line}
                </text>
              </g>
            ))}
            <rect x={colXs[4] + 20} y={bandInnerY + bandInnerH - 42}
                  width={colWs[4] - 40} height={30}
                  rx={6} ry={6} fill="#22c55e" opacity={0.16} />
            <text x={colXs[4] + colWs[4] / 2} y={bandInnerY + bandInnerH - 22}
                  textAnchor="middle" fill="#22c55e"
                  fontFamily="Figtree, sans-serif"
                  fontSize="13" fontWeight="700" letterSpacing="2">
              PURPOSE-LIMITED USE ONLY
            </text>

            {/* Arrows between columns */}
            {/* col1 -> col2 (outbound submission) */}
            <path d={`M ${colXs[0] + colWs[0] + 3} ${bandInnerY + bandInnerH / 2}
                     L ${colXs[1] - 3} ${bandInnerY + bandInnerH / 2}`}
                  stroke="#34d1c0" strokeWidth={2}
                  markerEnd="url(#mArrowCyan)" />
            {/* col2 -> col3 */}
            <path d={`M ${colXs[1] + colWs[1] + 3} ${bandInnerY + bandInnerH / 2}
                     L ${colXs[2] - 3} ${bandInnerY + bandInnerH / 2}`}
                  stroke={gold} strokeWidth={2}
                  markerEnd="url(#mArrow)" />
            {/* col3 <-> col4 (Ads Delivered / Signals Returned)
                Genesis M7 · scene 2 polish: arrows separated onto two
                vertical lanes with translucent backdrops so labels stop
                overlapping the "Meta Systems" and "facebook · instagram"
                column content. */}
            {(() => {
              const midX = (colXs[2] + colWs[2] + colXs[3]) / 2;
              const rightArrowY = bandInnerY + bandInnerH / 2 - 30;
              const leftArrowY = bandInnerY + bandInnerH / 2 + 30;
              return (
                <g>
                  <path d={`M ${colXs[2] + colWs[2] + 3} ${rightArrowY}
                           L ${colXs[3] - 3} ${rightArrowY}`}
                        stroke={gold} strokeWidth={2}
                        markerEnd="url(#mArrow)" />
                  <rect x={midX - 70} y={rightArrowY - 24} width={140} height={16}
                        rx={4} ry={4} fill={bgCardAlt} opacity={0.95} />
                  <text x={midX} y={rightArrowY - 12}
                        textAnchor="middle" fill={gold}
                        fontFamily="Figtree, sans-serif"
                        fontSize="12" fontWeight="700" letterSpacing="1.5">
                    Ads Delivered
                  </text>
                  <path d={`M ${colXs[3] - 3} ${leftArrowY}
                           L ${colXs[2] + colWs[2] + 3} ${leftArrowY}`}
                        stroke="#c084fc" strokeWidth={2}
                        markerEnd="url(#mArrow)" />
                  <rect x={midX - 80} y={leftArrowY + 6} width={160} height={16}
                        rx={4} ry={4} fill={bgCardAlt} opacity={0.95} />
                  <text x={midX} y={leftArrowY + 18}
                        textAnchor="middle" fill="#c084fc"
                        fontFamily="Figtree, sans-serif"
                        fontSize="12" fontWeight="700" letterSpacing="1.5">
                    Signals Returned
                  </text>
                </g>
              );
            })()}
            {/* col4 <-> col5 (Performance Data / Policy Signals) */}
            {(() => {
              const midX = (colXs[3] + colWs[3] + colXs[4]) / 2;
              const rightArrowY = bandInnerY + bandInnerH / 2 - 30;
              const leftArrowY = bandInnerY + bandInnerH / 2 + 30;
              return (
                <g>
                  <path d={`M ${colXs[3] + colWs[3] + 3} ${rightArrowY}
                           L ${colXs[4] - 3} ${rightArrowY}`}
                        stroke="#22c55e" strokeWidth={2}
                        markerEnd="url(#mArrowGreen)" />
                  <rect x={midX - 82} y={rightArrowY - 30} width={164} height={30}
                        rx={4} ry={4} fill={bgCardAlt} opacity={0.95} />
                  <text x={midX} y={rightArrowY - 18}
                        textAnchor="middle" fill="#22c55e"
                        fontFamily="Figtree, sans-serif"
                        fontSize="12" fontWeight="700" letterSpacing="1.5">
                    Performance Data
                  </text>
                  <text x={midX} y={rightArrowY - 4}
                        textAnchor="middle" fill={textMuted}
                        fontFamily="Figtree, sans-serif"
                        fontSize="11" fontWeight="500">
                    (through APIs)
                  </text>
                  <rect x={midX - 100} y={leftArrowY + 6} width={200} height={30}
                        rx={4} ry={4} fill={bgCardAlt} opacity={0.95} />
                  <text x={midX} y={leftArrowY + 18}
                        textAnchor="middle" fill={textMuted}
                        fontFamily="Figtree, sans-serif"
                        fontSize="11" fontWeight="600">
                    Policy & System Signals
                  </text>
                  <text x={midX} y={leftArrowY + 32}
                        textAnchor="middle" fill={textDim}
                        fontFamily="Figtree, sans-serif"
                        fontSize="10" fontWeight="500">
                    (Alerts / Updates)
                  </text>
                </g>
              );
            })()}
          </g>
        );
      })()}

      {/* ================================================================= */}
      {/* Data Handling Principles (6 columns)                               */}
      {/* ================================================================= */}
      <rect x={margin} y={dhY} width={W - margin * 2} height={dhH}
            rx={12} ry={12} fill={bgCardAlt} stroke={border} strokeWidth={1.5} />
      <text x={W / 2} y={dhY + 26} textAnchor="middle" fill={gold}
            fontFamily="Figtree, sans-serif"
            fontSize="18" fontWeight="700" letterSpacing="3">
        DATA HANDLING PRINCIPLES
      </text>
      {META_DATA_PRINCIPLES.map((p, pi) => {
        const cols = META_DATA_PRINCIPLES.length;
        const gap = 14;
        const cellW = (W - margin * 2 - gap * (cols + 1)) / cols;
        const cx = margin + gap + pi * (cellW + gap);
        const cy = dhY + 46;
        return (
          <g key={p.title}>
            <text x={cx} y={cy + 18} fill={p.color}
                  fontFamily="Figtree, sans-serif"
                  fontSize="13" fontWeight="700" letterSpacing="1">
              {p.title}
            </text>
            {p.items.map((it, ii) => (
              <g key={ii}>
                {p.xIcon ? (
                  // Red X for prohibited items
                  <g transform={`translate(${cx} ${cy + 34 + ii * 22 - 5})`}>
                    <path d="M 0 0 L 8 8 M 8 0 L 0 8"
                          stroke={p.color} strokeWidth={2}
                          strokeLinecap="round" />
                  </g>
                ) : (
                  <circle cx={cx + 4} cy={cy + 32 + ii * 22}
                          r={2.5} fill={p.color} />
                )}
                <text x={cx + 16} y={cy + 36 + ii * 22}
                      fill={textBody}
                      fontFamily="Figtree, sans-serif"
                      fontSize="12" fontWeight="500">
                  {it}
                </text>
              </g>
            ))}
          </g>
        );
      })}

      {/* ================================================================= */}
      {/* Compliance pillars (4 columns)                                     */}
      {/* ================================================================= */}
      {META_COMPLIANCE_PILLARS.map((p, pi) => {
        const cols = META_COMPLIANCE_PILLARS.length;
        const gap = 14;
        const cellW = (W - margin * 2 - gap * (cols - 1)) / cols;
        const cx = margin + pi * (cellW + gap);
        return (
          <g key={p.title}>
            <rect x={cx} y={compY} width={cellW} height={compH}
                  rx={10} ry={10} fill={bgCardAlt}
                  stroke={p.color} strokeWidth={1.4} />
            <text x={cx + 16} y={compY + 26} fill={p.color}
                  fontFamily="Figtree, sans-serif"
                  fontSize="14" fontWeight="700" letterSpacing="1">
              {p.title}
            </text>
            {p.items.map((it, ii) => (
              <g key={ii}>
                <circle cx={cx + 22} cy={compY + 48 + ii * 22}
                        r={3} fill={p.color} />
                <text x={cx + 34} y={compY + 52 + ii * 22}
                      fill={textBody}
                      fontFamily="Figtree, sans-serif"
                      fontSize="13" fontWeight="500">
                  {it}
                </text>
              </g>
            ))}
          </g>
        );
      })}

      {/* ================================================================= */}
      {/* Governed Learning Loop footer                                      */}
      {/* ================================================================= */}
      <rect x={margin} y={loopY} width={W - margin * 2} height={loopH}
            rx={10} ry={10} fill="#0e1620" stroke="#34d1c0" strokeWidth={1.3} />
      <text x={margin + 22} y={loopY + 26} fill="#34d1c0"
            fontFamily="Figtree, sans-serif"
            fontSize="15" fontWeight="700" letterSpacing="1.5">
        GOVERNED LEARNING LOOP
      </text>
      <text x={margin + 22} y={loopY + 48} fill={textMuted}
            fontFamily="Figtree, sans-serif"
            fontSize="12" fontWeight="500">
        Insights inform future strategy.
      </text>
      <text x={W / 2} y={loopY + 26} textAnchor="middle" fill={textMain}
            fontFamily="Figtree, sans-serif"
            fontSize="14" fontWeight="600">
        Performance signals and policy updates flow back to Brand OS as inputs for future campaigns.
      </text>
      <text x={W / 2} y={loopY + 46} textAnchor="middle" fill={textMuted}
            fontFamily="Figtree, sans-serif"
            fontSize="12" fontWeight="500">
        All new or modified strategies, creative, and campaigns must re-enter the governed lifecycle at Stage 01.
      </text>
      <text x={W - margin - 22} y={loopY + 26} textAnchor="end" fill="#34d1c0"
            fontFamily="Figtree, sans-serif"
            fontSize="15" fontWeight="700" letterSpacing="1.5">
        LEARNING DOES NOT BYPASS GOVERNANCE.
      </text>
      <text x={W - margin - 22} y={loopY + 48} textAnchor="end" fill={textMuted}
            fontFamily="Figtree, sans-serif"
            fontSize="12" fontWeight="500">
        Automation operates within the rules we set.
      </text>
    </svg>
  );
}

// Scene 2 body wrapper
function SceneDataLifecycle() {
  return (
    <>
      <div style={{
        padding: "24px 16px", background: "#0a0d13",
        borderRadius: 12,
      }}>
        <DataLifecycleDiagram />
      </div>
      <div className="bo-hint" style={{ marginTop: 12, textAlign: "center" }}>
        Sibling to Scene 1 — this graphic explains how Brand OS handles Meta
        data end-to-end (integration boundary, retention, security, purpose
        limitation). Downloadable as a stand-alone PNG for the App Review
        submission package.
      </div>
    </>
  );
}

// -------------------------------------------------------------------
// Scene components
// ---------------------------------------------------------------------------

function LiveStat({ label, value, sub }) {
  return (
    <div className="bo-metric" style={{ minWidth: 140 }}>
      <div className="bo-metric-label">{label}</div>
      <div className="bo-metric-value">{value ?? "—"}</div>
      {sub && (
        <div style={{ fontSize: 12, color: "var(--bo-muted)", marginTop: 2 }}>{sub}</div>
      )}
    </div>
  );
}

function SceneOverview() {
  return (
    <>
      <div style={{
        padding: "24px 16px", background: "#0f1116",
        borderRadius: 12,
      }}>
        <FullLifecycleDiagram />
      </div>
      <div style={{ marginTop: 24, color: "var(--bo-muted)", lineHeight: 1.6, fontSize: 14 }}>
        Every stage below is an actual endpoint + UI in Brand OS. The <strong style={{ color: "var(--bo-text)" }}>Attribute</strong> stage is where <code>read_insights</code> is used — reading engagement metrics from the same Meta Page owned by our verified business, so the Page owner can see whether the frozen creative worked. No third-party data. No advertising outside the Page owner's own account.
      </div>
    </>
  );
}

function SceneFoundation({ data }) {
  return (
    <>
      <FlowDiagramSvg activeKey="foundation" />
      <div className="bo-metric-row" style={{ marginTop: 24 }}>
        <LiveStat label="Brand vault products" value={data?.counts?.products ?? "—"} />
        <LiveStat label="Audiences defined" value={data?.counts?.audiences ?? "—"} />
        <LiveStat label="Voice patterns" value={data?.counts?.voice ?? "—"} />
      </div>
      <p style={{ color: "var(--bo-muted)", marginTop: 20, lineHeight: 1.6, fontSize: 14 }}>
        Foundation is where the business defines who they are, who they're talking to, and what they sell. Everything downstream — every campaign, every post, every attribution row — carries lineage back to these frozen documents.
      </p>
    </>
  );
}

function SceneCreate({ data }) {
  return (
    <>
      <FlowDiagramSvg activeKey="create" />
      <div className="bo-metric-row" style={{ marginTop: 24 }}>
        <LiveStat label="Campaigns" value={data?.counts?.campaigns ?? "—"} />
        <LiveStat label="Asset sets" value={data?.counts?.assetSets ?? "—"} />
        <LiveStat label="Approved variants" value={data?.counts?.approvedVariants ?? "—"} />
      </div>
      <p style={{ color: "var(--bo-muted)", marginTop: 20, lineHeight: 1.6, fontSize: 14 }}>
        The operator drafts a campaign, generates two-to-four creative variants (text + image), and moves the strongest into an asset set. Everything is version-pinned so the exact bytes that go through Preflight are the exact bytes that get published.
      </p>
    </>
  );
}

function ScenePreflight() {
  return (
    <>
      <FlowDiagramSvg activeKey="preflight" />
      <ul className="bo-list-tight" style={{ marginTop: 24 }}>
        <li><span className="bo-conn-name">Legal check</span><Pill tone="success">enforced</Pill></li>
        <li><span className="bo-conn-name">Brand voice check</span><Pill tone="success">enforced</Pill></li>
        <li><span className="bo-conn-name">Frozen SHA-256 package</span><Pill tone="success">signed</Pill></li>
        <li><span className="bo-conn-name">Channel policy check</span><Pill tone="success">enforced</Pill></li>
      </ul>
      <p style={{ color: "var(--bo-muted)", marginTop: 20, lineHeight: 1.6, fontSize: 14 }}>
        Preflight is the immutability gate. The exact package is hashed, signed, and locked. The same hash follows the post all the way to the attribution row — reviewers can trace any live metric back to the exact bytes that were published.
      </p>
    </>
  );
}

function ScenePublish({ data }) {
  return (
    <>
      <FlowDiagramSvg activeKey="publish" />
      <div className="bo-metric-row" style={{ marginTop: 24 }}>
        <LiveStat label="Publish receipts" value={data?.counts?.receipts ?? "—"} />
        <LiveStat label="Successful Facebook posts" value={data?.counts?.fbSuccess ?? "—"} />
        <LiveStat label="Bound Page connections" value={data?.counts?.channels ?? "—"} />
      </div>
      <p style={{ color: "var(--bo-muted)", marginTop: 20, lineHeight: 1.6, fontSize: 14 }}>
        <code>pages_manage_posts</code> is used here — Facebook Login binds the Precipice Health Page to Brand OS and publish adapters post exclusively to that same owned Page. Every publish attempt lands a receipt with a SHA-256 anchor over the frozen package.
      </p>
    </>
  );
}

function SceneAttribute({ data }) {
  const insightsGranted = data?.readInsightsGranted;
  return (
    <>
      <FlowDiagramSvg activeKey="attribute" />
      <div style={{
        marginTop: 24, padding: 20,
        background: "var(--bo-surface-2)", borderRadius: 12,
        border: "1px solid var(--bo-border)",
      }}>
        <div style={{ fontSize: 13, letterSpacing: 1.5, color: "var(--bo-muted)", marginBottom: 12 }}>
          THIS SCOPE IS WHY WE'RE SUBMITTING
        </div>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div>
            <div className="bo-metric-label">read_insights · granular grant</div>
            <div style={{ marginTop: 6 }}>
              <Pill tone={insightsGranted ? "success" : "warn"}>
                {insightsGranted ? "granted" : "pending review"}
              </Pill>
            </div>
          </div>
          <div>
            <div className="bo-metric-label">Metrics currently reading</div>
            <div style={{ marginTop: 6, fontSize: 13, color: "var(--bo-text)" }}>
              post_clicks · post_reactions_by_type_total · post_activity_by_action_type
            </div>
          </div>
          <div>
            <div className="bo-metric-label">Metrics unlocked once granted</div>
            <div style={{ marginTop: 6, fontSize: 13, color: "var(--bo-text)" }}>
              post_impressions · post_impressions_unique · post_engaged_users · post_video_views
            </div>
          </div>
        </div>
      </div>
      <p style={{ color: "var(--bo-muted)", marginTop: 20, lineHeight: 1.6, fontSize: 14 }}>
        On every attribution pull the adapter probes Meta's <code>debug_token</code> and only requests metrics it can actually receive — that means no wasted API calls, no misleading data, and honest reporting to the Page owner when a scope isn't yet granted. Attribution rows are stored append-only with a SHA-256 of Meta's exact response for provenance.
      </p>
    </>
  );
}

function SceneInsights({ data }) {
  return (
    <>
      <FlowDiagramSvg activeKey="insights" />
      <div className="bo-metric-row" style={{ marginTop: 24 }}>
        <LiveStat label="Attribution rows" value={data?.rollup?.totals?.attribution_rows ?? "—"} />
        <LiveStat label="Receipts covered" value={data?.rollup?.totals?.receipts ?? "—"} />
        <LiveStat label="Campaigns rolled up" value={data?.rollup?.totals?.campaigns ?? "—"} />
        <LiveStat label="Spend visible" value={data?.rollup?.totals?.spend_usd != null ? `$${(data.rollup.totals.spend_usd).toFixed(2)}` : "—"} />
      </div>
      <p style={{ color: "var(--bo-muted)", marginTop: 20, lineHeight: 1.6, fontSize: 14 }}>
        The Insights dashboard is the surface the Page owner sees — cost per click, cost per engagement, best variant per campaign. This is <strong style={{ color: "var(--bo-text)" }}>the payoff</strong> of <code>read_insights</code>: the same business that owns the Page finally sees whether the frozen creative earned real audience outcomes.
      </p>
    </>
  );
}

function SceneClose() {
  return (
    <>
      <div style={{
        padding: 36, background: "var(--bo-surface-2)", borderRadius: 12,
      }}>
        <div style={{ fontSize: 13, letterSpacing: 1.5, color: "var(--bo-muted)", marginBottom: 16 }}>
          DATA HANDLING · IN ONE FRAME
        </div>
        <ul style={{ listStyle: "none", padding: 0, margin: 0, lineHeight: 1.9, fontSize: 14, color: "var(--bo-text)" }}>
          <li>✓ Metrics are stored append-only with a SHA-256 anchor of Meta's exact response</li>
          <li>✓ Metrics are displayed only to the same business account that owns the Page</li>
          <li>✓ No third-party sharing, no resale, no advertising outside the Page owner's own account</li>
          <li>✓ <code>read_insights</code> is used only to read insights for Pages the authenticated business owns</li>
          <li>✓ Token scopes are probed on every request — the adapter never asks for metrics it can't lawfully receive</li>
        </ul>
      </div>
      <p style={{ color: "var(--bo-muted)", marginTop: 20, lineHeight: 1.6, fontSize: 14 }}>
        End of walkthrough. Test credentials are in the App Review submission notes. Thank you for reviewing.
      </p>
    </>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function ReviewWalkthrough() {
  const [scene, setScene] = useState(0);
  const [data, setData] = useState({});
  const [err, setErr] = useState(null);
  const svgRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Fetch everything in parallel; any single failure degrades to "—".
        const [campaigns, audiences, receipts, connections, rollup, worker] = await Promise.all([
          api.get("/campaigns").catch(() => ({ data: null })),
          api.get("/audiences").catch(() => ({ data: null })),
          api.get("/publish-receipts?limit=50").catch(() => ({ data: null })),
          api.get("/publish/channel-connections").catch(() => ({ data: null })),
          api.get("/attributions/rollups/campaigns").catch(() => ({ data: null })),
          api.get("/attributions/worker/status").catch(() => ({ data: null })),
        ]);
        if (!alive) return;

        const receiptList = receipts?.data?.receipts || receipts?.data?.publish_receipts || [];
        const connList = connections?.data?.connections || connections?.data?.channel_connections || [];
        const campaignList = campaigns?.data?.campaigns || campaigns?.data || [];
        const audienceList = audiences?.data?.audiences || audiences?.data || [];

        // Infer read_insights grant from the latest attribution row's scope probe.
        // (Rollup endpoint doesn't expose that directly; instead we fetch one raw row.)
        let readInsightsGranted = false;
        try {
          const a = await api.get("/attributions?limit=1");
          const row = a?.data?.attributions?.[0];
          readInsightsGranted = Boolean(row?.source_query?.read_insights_granted);
        } catch {}

        setData({
          counts: {
            products: 0,
            audiences: Array.isArray(audienceList) ? audienceList.length : (audienceList?.count ?? 0),
            voice: 0,
            campaigns: Array.isArray(campaignList) ? campaignList.length : (campaignList?.count ?? 0),
            assetSets: (campaignList || []).reduce((n, c) => n + (c?.asset_set_count || 0), 0),
            approvedVariants: 0,
            receipts: receiptList.length,
            fbSuccess: receiptList.filter((r) => r.channel === "facebook" && r.execution_status === "published").length,
            channels: connList.length,
          },
          rollup: rollup?.data || {},
          worker: worker?.data || {},
          readInsightsGranted,
        });
      } catch (e) {
        if (alive) setErr(formatApiError(e));
      }
    })();
    return () => { alive = false; };
  }, []);

  const scenes = useMemo(() => ([
    { title: "The governed publishing flow", subtitle: "Seven stages. One audit trail. Every metric traceable to its exact bytes.", body: <SceneOverview />, exportName: "brand-os-governed-publishing-flow.png" },
    { title: "Brand OS × Meta data lifecycle", subtitle: "How Brand OS handles Meta data end-to-end — integration boundary, retention, security, purpose limitation.", body: <SceneDataLifecycle />, exportName: "brand-os-meta-data-lifecycle.png" },
    { title: "Foundation", subtitle: "Where the business defines itself.", body: <SceneFoundation data={data} /> },
    { title: "Create", subtitle: "Where creative is drafted, versioned, and short-listed.", body: <SceneCreate data={data} /> },
    { title: "Preflight", subtitle: "Where the exact bytes are frozen and signed.", body: <ScenePreflight /> },
    { title: "Publish", subtitle: "Where content lands on Meta via pages_manage_posts.", body: <ScenePublish data={data} /> },
    { title: "Attribute", subtitle: "Where read_insights closes the loop.", body: <SceneAttribute data={data} /> },
    { title: "Insights", subtitle: "The dashboard the Page owner sees.", body: <SceneInsights data={data} /> },
    { title: "Data handling", subtitle: "One-slide compliance summary.", body: <SceneClose /> },
  ]), [data]);

  const current = scenes[scene];
  const total = scenes.length;

  function goPrev() { setScene((s) => Math.max(0, s - 1)); }
  function goNext() { setScene((s) => Math.min(total - 1, s + 1)); }

  useEffect(() => {
    function onKey(e) {
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  // Convert the flow-diagram SVG into a PNG file and trigger a download.
  // Uses the SVG's declared viewBox for the output pixel dimensions so
  // the PNG is guaranteed high-resolution regardless of how the SVG is
  // currently sized in the browser window.
  function downloadFlowPng(filename = "brand-os-governed-publishing-flow.png") {
    const svg = document.querySelector('[data-testid="walkthrough-flow-diagram"]');
    if (!svg) return;
    // Clone so we can set explicit width/height without mutating the DOM copy.
    const clone = svg.cloneNode(true);
    const viewBox = (svg.getAttribute("viewBox") || "0 0 2000 1800")
      .split(" ").map(Number);
    const vbW = viewBox[2] || 2000;
    const vbH = viewBox[3] || 1960;
    // 2x scale for retina-crisp export (~4000×3600 PNG).
    const scale = 2;
    const outW = vbW * scale;
    const outH = vbH * scale;
    clone.setAttribute("width", outW);
    clone.setAttribute("height", outH);
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(clone);
    const svgBlob = new Blob(
      ['<?xml version="1.0" standalone="no"?>\n', svgString],
      { type: "image/svg+xml;charset=utf-8" },
    );
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#0f1116";
      ctx.fillRect(0, 0, outW, outH);
      ctx.drawImage(img, 0, 0, outW, outH);
      URL.revokeObjectURL(url);
      const dl = document.createElement("a");
      dl.href = canvas.toDataURL("image/png");
      dl.download = filename;
      dl.click();
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      // eslint-disable-next-line no-alert
      alert(
        "PNG export failed. Right-click the diagram and choose \"Save image\" as a fallback."
      );
    };
    img.src = url;
  }

  return (
    <Shell>
      <div className="bo-page" data-testid="review-walkthrough-page">
        <header className="bo-page-header">
          <div>
            <div className="bo-eyebrow">META APP REVIEW · WALKTHROUGH</div>
            <h1 data-testid="review-walkthrough-title">{current.title}</h1>
            <p className="bo-page-lede">{current.subtitle}</p>
          </div>
          <div className="bo-page-actions" style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {current.exportName && (
              <button
                className="bo-btn bo-btn-secondary"
                onClick={() => downloadFlowPng(current.exportName)}
                data-testid="walkthrough-download-png"
                title="Save the current diagram as a PNG for the App Review submission"
              >
                <Download size={14} style={{ marginRight: 6 }} />
                Download PNG
              </button>
            )}
            <span style={{ fontSize: 13, color: "var(--bo-muted)", padding: "0 12px" }}>
              Scene {scene + 1} of {total}
            </span>
            <button className="bo-btn bo-btn-secondary" onClick={goPrev} disabled={scene === 0}
                    data-testid="walkthrough-prev">
              <ChevronLeft size={16} />
            </button>
            <button className="bo-btn bo-btn-primary" onClick={goNext} disabled={scene === total - 1}
                    data-testid="walkthrough-next">
              <ChevronRight size={16} />
            </button>
          </div>
        </header>

        {err && <ErrorBanner error={err} testId="walkthrough-error" />}

        {/* Progress dots */}
        <div style={{ display: "flex", gap: 8, margin: "12px 0 24px" }}
             data-testid="walkthrough-progress">
          {scenes.map((_, i) => (
            <button
              key={i}
              onClick={() => setScene(i)}
              data-testid={`walkthrough-jump-${i}`}
              aria-label={`Jump to scene ${i + 1}`}
              style={{
                background: "none", border: "none", padding: 0, cursor: "pointer",
                lineHeight: 0,
              }}
            >
              <Circle
                size={10}
                fill={i === scene ? "var(--bo-accent)" : "transparent"}
                color={i === scene ? "var(--bo-accent)" : "var(--bo-muted)"}
              />
            </button>
          ))}
        </div>

        {/* Scene */}
        <section className="bo-card" style={{ padding: 32, minHeight: 480 }}
                 data-testid={`walkthrough-scene-${scene}`}>
          {current.body}
        </section>

        {/* Speaker cue — visible narration for the reviewer + you */}
        <div style={{ marginTop: 16, fontSize: 13, color: "var(--bo-muted)" }}>
          Tip: press <kbd style={{ padding: "2px 6px", border: "1px solid var(--bo-border)", borderRadius: 4 }}>→</kbd> or <kbd style={{ padding: "2px 6px", border: "1px solid var(--bo-border)", borderRadius: 4 }}>←</kbd> to move between scenes while recording. <Link to="/brand-os/insights">Open Insights dashboard →</Link>
        </div>
      </div>
    </Shell>
  );
}
